customElements.define(
  'sticky-header',
  class StickyHeader extends HTMLElement {
    constructor() {
      super();
      this.header = document.querySelector('.section-header');
      this.stickyType = this.dataset.stickyType;
      this.headerBounds = {};
      this.currentScrollTop = 0;
      this.preventReveal = false;
      this.isScrolling = null;
      this.onScrollHandler = this.onScroll.bind(this);
      this.hideHeaderOnScrollUp = () => (this.preventReveal = true);
      this.onResize = this.debounce(this.setHeaderHeight.bind(this), 100);
    }

    connectedCallback() {
      if (!this.header) return;
      this.setHeaderHeight();
      this.createObserver();
      this.resizeObserver = new ResizeObserver(this.onResize);
      this.resizeObserver.observe(this.header);
      this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
      window.addEventListener('scroll', this.onScrollHandler, { passive: true });
    }

    disconnectedCallback() {
      this.removeEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
      window.removeEventListener('scroll', this.onScrollHandler);
      this.resizeObserver.disconnect();
    }

    debounce(func, wait) {
      let timeout;
      return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(this, args), wait);
      };
    }

    setHeaderHeight() {
      requestAnimationFrame(() => {
        document.documentElement.style.setProperty('--header-height', `${this.header.offsetHeight}px`);
      });
    }

    createObserver() {
      const observer = new IntersectionObserver(([entry]) => {
        this.headerBounds = entry.intersectionRect;
        if (this.headerBounds.top === 0 && this.headerBounds.bottom === 0) {
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          const { top, bottom } = entry.boundingClientRect;
          this.headerBounds = { top: scrollTop + top, bottom: scrollTop + bottom };
        }
        observer.disconnect();
      });

      observer.observe(this.header);
    }

    onScroll() {
      if (!this.headerBounds.bottom) return;

      requestAnimationFrame(() => {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const scrolledPast = scrollTop > this.headerBounds.bottom;

        if (this.stickyType === 'on-scroll-up') {
          if (scrollTop > this.currentScrollTop && scrolledPast) {
            this.header.classList.add('scrolled-past-header');
            if (!this.preventHide) this.hide();
          } else if (scrollTop < this.currentScrollTop && scrolledPast) {
            this.header.classList.add('scrolled-past-header');
            if (!this.preventReveal) {
              this.reveal();
            } else {
              clearTimeout(this.isScrolling);
              this.isScrolling = setTimeout(() => (this.preventReveal = false), 100);
              this.hide();
            }
          } else if (scrollTop <= this.headerBounds.top) {
            this.header.classList.remove('scrolled-past-header');
            this.reset();
          }
        } else {
          if (scrolledPast) {
            this.header.classList.add('scrolled-past-header');
            if (!this.sticky()) {
              this.hide();
              setTimeout(() => this.reveal(), 100);
            }
          } else if (scrollTop <= this.headerBounds.top) {
            this.header.classList.remove('scrolled-past-header');
            this.reset();
          }
        }

        this.currentScrollTop = scrollTop;
      });
    }

    hide() {
      this.header.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
      document.body.classList.add('shopify-body-section-header-hidden', 'shopify-body-section-header-sticky');
      this.closeMenuItems();
      this.closeSearchModal();
      this.closeHeaderDrawer();
    }

    reveal() {
      this.header.classList.add('shopify-section-header-sticky', 'animate');
      this.header.classList.remove('shopify-section-header-hidden');
      document.body.classList.add('shopify-body-section-header-sticky');
      document.body.classList.remove('shopify-body-section-header-hidden');
    }

    reset() {
      this.header.classList.remove('shopify-section-header-hidden', 'shopify-section-header-sticky', 'animate');
    }

    sticky() {
      return (
        this.header.classList.contains('shopify-section-header-sticky') &&
        !this.header.classList.contains('shopify-section-header-hidden') &&
        document.body.classList.contains('shopify-body-section-header-sticky') &&
        !document.body.classList.contains('shopify-body-section-header-hidden')
      );
    }

    closeHeaderDrawer() {
      this.headerDrawer = this.headerDrawer || this.header.querySelectorAll('header-drawer');
      this.headerDrawer.forEach((drawer) => drawer.isOpen && drawer.close());
    }

    closeMenuItems() {
      this.menuItems = this.menuItems || this.header.querySelectorAll('header-menu, header-submenu');
      this.menuItems.forEach((menuItem) => menuItem.closeDropdown());
    }

    closeSearchModal() {
      this.searchModals = this.searchModals || this.header.querySelectorAll('predictive-search');
      this.searchModals.forEach((searchModal) => searchModal.isOpen && searchModal.close(false));
    }
  }
);

customElements.define('header-menu',
  class HeaderMenu extends HTMLElement {
    constructor() {
      super();
      this.header = document.querySelector('.header-wrapper');
      this.setPseudoclass();
      this.addEventListener('focusin', this.openDropdown.bind(this));
      this.addEventListener('focusout', this.closeDropdown.bind(this));
    }

    openDropdown() {
      this.classList.add('active');
    }

    closeDropdown() {
      this.classList.remove('active');
    }

    setPseudoclass() {
      if (!this.header) return;
      if (this.header.firstElementChild.classList.contains('header--top-center') || this.header.firstElementChild.classList.contains('header--top-left')) {
        this.style.setProperty('--p-header-menu', `var(--padding-bottom)`);
      } else {
        this.style.setProperty('--p-header-menu', `${Math.floor((this.header.offsetHeight - this.offsetHeight) / 2)}px`);
      }
    }
  }
)

customElements.define('header-submenu',
  class HeaderSubMenu extends HTMLElement {
    constructor() {
      super();
      this.addEventListener('focusin', this.openDropdown.bind(this));
      this.addEventListener('focusout', this.closeDropdown.bind(this));
    }

    openDropdown() {
      this.classList.add('active');
    }

    closeDropdown() {
      this.classList.remove('active');
    }
  }
)